import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { GlobalService } from '../../../global/services/global.service';
import { AppService } from '../../../app.service';
import { generalConfigFromGlobal } from '../../../shared/workspace-element-context';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { SubmissionService } from '../../submission-data/submission.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import {
  EfileGridCallbackEvent,
  EfileGridConfig,
  EfileGridDirective,
} from '../../shared/efile-grid.directive';
import { Submission } from '../../models/submission.model';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import {
  CtrlFnPlan,
  EfileCtrlFnDispatcherService,
} from '../scrubbing-actions/efile-ctrlfn-dispatcher.service';

import {
  EfileWorkflowService,
  ReturnReviewStep,
} from './efile-workflow.service';
import {
  RETURN_REVIEW_DASHBOARD_EXPORT_HEADERS,
  pickLatestIrsSubmissionOption,
  resolveDashboardIrsSelected,
} from './return-review-dashboard.model';
import {
  DfSelectSingleFilter,
  DfSelectSingleOption,
} from '../../../shared/_directives/df-select-single/df-select-single-filter.model';

/**
 * Static mapping of legacy `return-review.*` leaf state names to their
 * gizmoGrid configuration (mirrors the `resolve.api_url` + `resolve.params`
 * blocks in `ui/app/components/efile/efile.js`). The key is the state's
 * leaf name (e.g. `efile-diagnostics`) — we match on suffix so the backend
 * can return either the full state URL
 * (`submissions-dasboard.submission.return-review.efile-diagnostics`) or
 * just the leaf.
 *
 * Notes:
 *   - `validation` (aka `errors`) uses `data/errors.html` — a different
 *     component, not a grid — so it is intentionally excluded here.
 *   - Scenario-keyed extras (e.g. `total-review-diag`'s `scenario_id`) are
 *     layered in at build time in {@link buildGridConfig}.
 */
interface ReturnReviewGridBase {
  gridId: string;
  actionId?: number;
  actionCode?: string;
  /** Extra per-state params that don't depend on submission context. */
  extraParamKeys?: Array<'sso_id' | 'scenario_id'>;
  /**
   * Tab-level filters declared inline in the legacy ui-router state config
   * (`resolve.filters`, e.g. efile.js L1005-L1033 / L1063-L1091 for the
   * `xml-vs-elements*` states). Each entry mirrors a single AngularJS
   * filter object: it's loaded on tab activation, surfaced as a
   * `df-select-single` dropdown above the grid host, and the user's
   * chosen value is merged into `extraParams` before the grid fetches
   * data — same UX as `ctrl.filters` + `Filter` button in
   * `EfileWorkflowController.js > EfileCustomGrid`.
   */
  filters?: ReturnReviewFilterSpec[];
}

/**
 * Declarative spec for a tab-level filter that the return-review screen
 * loads + renders above its grid. Mirrors the inline `filters: [...]`
 * block on the legacy ui-router states (efile.js L1005 / L1063), but
 * limited to the fields we actually need today: a single-select dropdown
 * loaded from a `loadJsonObject?action_code=...` action.
 */
interface ReturnReviewFilterSpec {
  /** URL parameter name written into the grid call (e.g. `irs_id`). */
  paramName: string;
  /** Display label for the dropdown (e.g. "IRS Submissions"). */
  label: string;
  /** action_code passed to `loadJsonObject` to fetch the option list. */
  actionCode: string;
  /** When true, the grid is gated until the user picks a value. */
  required?: boolean;
}

const RETURN_REVIEW_GRID_CONFIGS: Record<string, ReturnReviewGridBase> = {
  // efile.js L628
  'efile-diagnostics':          { gridId: '5712', actionId: 31996 },
  // efile.js L682
  'efile-sno-dups':             { gridId: '6169', actionId: 33404, extraParamKeys: ['sso_id'] },
  // efile.js L755
  'submission-errors':          { gridId: '6171', actionId: 33409, extraParamKeys: ['sso_id'] },
  // efile.js L791
  'total-review-diag':          { gridId: '6183', actionId: 33443, extraParamKeys: ['sso_id', 'scenario_id'] },
  // efile.js L828
  'amounts-reconciliation':     { gridId: '6136', actionId: 33333, extraParamKeys: ['sso_id'] },
  // efile.js L864
  'py-cy':                      { gridId: '6135', actionId: 33332, extraParamKeys: ['sso_id'] },
  // efile.js L913
  'scrubbings':                 { gridId: '6175', actionId: 33421, extraParamKeys: ['sso_id'] },
  // efile.js L986 — IRS Submissions filter (efile.js L1005-L1033).
  'xml-vs-elements':            {
    gridId: '6134',
    actionId: 33331,
    filters: [
      { paramName: 'irs_id', label: 'IRS Submissions', actionCode: 'j4k0an', required: true },
    ],
  },
  // efile.js L1044 — IRS Submissions filter (efile.js L1063-L1091).
  'xml-vs-elements-counts':     {
    gridId: '6137',
    actionId: 33335,
    filters: [
      { paramName: 'irs_id', label: 'IRS Submissions', actionCode: 'j4k0an', required: true },
    ],
  },
};

/**
 * URL-slug ↔ state-name-leaf map for the two return-review states whose
 * legacy `url:` (efile.js) differs from the state-name leaf the backend
 * returns in `STATE_URL`. Used to:
 *   1. Build the correct router URL when the user clicks a tab — preserves
 *      legacy deep-link parity (`/return-review/errors`, not
 *      `/return-review/validation`).
 *   2. Resolve an inbound child-subState slug back to the matching step so
 *      `applyChildSubState('errors')` selects the validation step.
 *
 * Steps not in this map use the leaf verbatim for both directions.
 */
const STATE_LEAF_TO_URL_SLUG: Record<string, string> = {
  'validation': 'errors',          // efile.js L621-L622
  'scrubbings': 'efile-sub-scrubbings', // efile.js L913-L914
};
const URL_SLUG_TO_STATE_LEAF: Record<string, string> = Object.keys(STATE_LEAF_TO_URL_SLUG).reduce(
  (acc, leaf) => {
    acc[STATE_LEAF_TO_URL_SLUG[leaf]] = leaf;
    return acc;
  },
  {} as Record<string, string>,
);

/**
 * Canonical key map for return-review leaves.
 * This keeps URL slugs (`errors`, `efile-sub-scrubbings`) and state-name
 * leaves (`validation`, `scrubbings`) interchangeable across:
 *   - deep-link matching
 *   - step selection
 *   - grid config lookup
 */
const RETURN_REVIEW_LEAF_ALIASES: Record<string, string> = {
  errors: 'validation',
  validation: 'validation',
  'efile-sub-scrubbings': 'scrubbings',
  scrubbings: 'scrubbings',
};

/** Legacy grid-tpl export metadata shared by all return-review step grids. */
const RETURN_REVIEW_LEGACY_EXPORT = {
  legacyGridTplExport: true as const,
  exportScreenKey: 11212,
  exportDisplayDefaults: {
    display_mE_code: 'true',
    display_scenario_description: 'true',
    display_filing_group_description: '1',
  },
};

/** IRS Submissions dropdown on the Return Review Dashboard export bar. */
const DASHBOARD_IRS_FILTER_SPEC: ReturnReviewFilterSpec = {
  paramName: 'irs_id',
  label: 'IRS Submissions',
  actionCode: 'j4k0an',
};

/**
 * Angular 12 port of:
 *   - `ui/app/components/efile/efileElements/return-review.html`
 *   - `EfileReturnReviewController` in
 *     `ui/app/components/efile/EfileWorkflowController.js`
 *
 * Responsibilities:
 *   1. Load workflow steps for the active submission on init.
 *   2. Render a horizontal tab-strip (one tab per step) that mirrors the
 *      legacy status icons / colour semantics (pending / resolved / ignored /
 *      failed).
 *   3. Own the "selected step" state so a sibling content area can render
 *      the actual grid for the currently active step (this is the target
 *      surface for a follow-up "per-step grid mount" pass).
 *   4. Expose an "Ignore" action on FAILED steps that captures an
 *      explanation and posts it via {@link EfileWorkflowService.ignoreStep}
 *      (equivalent to the legacy modal + `ignoreWithExplanation` call).
 *
 * Out of scope (tracked as follow-ups):
 *   - Mounting the actual `<div gg-grid>` for each step. Today the tab
 *     content region shows a placeholder; the plumbing needed to mount
 *     gizmoGrid per step is the same Path B pattern used in
 *     `SubmissionDataComponent` and can be layered in without changing
 *     this component's public API.
 */
@Component({
    selector: 'gtw-efile-return-review',
    templateUrl: './efile-return-review.component.html',
    styleUrls: ['./efile-return-review.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class EfileReturnReviewComponent implements OnInit, OnChanges, OnDestroy {
  private readonly subs = new Subscription();

  /**
   * Pattern B deep-link key forwarded from `SubmissionDetailComponent`.
   * Mirrors the leaf segment of legacy URLs like
   * `/return-review/efile-diagnostics`, `/return-review/py-cy`, etc. When
   * set, after `loadSteps()` resolves we match it against the loaded
   * step's `STATE_URL` suffix and pre-select that step. Empty / unmatched
   * values fall back to the default first-step selection.
   */
  @Input() childSubState: string = '';
  @Input() submission?: Submission;

  steps: ReturnReviewStep[] = [];
  loading = false;
  loadError: string | null = null;
  selectedStateUrl: string | null = null;
  showIgnoreButton = false;
  /** True when the static Dashboard tab is active (default view). */
  isDashboardSelected = true;
  /** True while an export request is in flight. */
  exporting = false;
  /** True while the dashboard refresh button triggered a silent steps reload. */
  dashboardRefreshing = false;

  /** IRS Submissions filter for dashboard export (same `j4k0an` list as xml-vs-elements). */
  dashboardIrsFilter: DfSelectSingleFilter | null = null;
  dashboardIrsFilterLoading = false;
  private dashboardIrsFilterLoaded = false;
  /** True after the user explicitly changes the dashboard IRS dropdown. */
  dashboardIrsUserPicked = false;

  /** Cached grid configs keyed by step.STATE_URL, built once after steps
   *  load. We rebuild when the active submission changes. */
  private gridConfigByStateUrl: Record<string, EfileGridConfig | null> = {};

  /**
   * Runtime state for tab-level filters (mirrors `ctrl.filters` /
   * `ctrl.filtersLoaded` in legacy `EfileCustomGrid`). Keyed by
   * `step.STATE_URL` so the user's selections survive a tab swap and
   * round-trip back to the same state. Each `DfSelectSingleFilter` is
   * mutated in place by the `[df-select-single]` directive when the
   * user picks a value, but the `selected` array is not yet committed
   * to the grid until the user clicks the **Filter** button — same UX
   * as the legacy template's "Filter" button binding.
   */
  filtersByStateUrl: Record<string, DfSelectSingleFilter[]> = {};
  /** Per-state spinner that shadows the legacy `!ctrl.filtersLoaded` flag. */
  filtersLoadingByStateUrl: Record<string, boolean> = {};
  /** Tracks whether each state's filter values have been fetched at least once. */
  private filtersLoadedByStateUrl: Record<string, boolean> = {};

  /**
   * Tab-level filter selections are only applied to the grid after the user
   * clicks **Filter** (legacy `grid-tpl.html`). `df-select-single` updates
   * `filtersByStateUrl` live; we must not hide/show the grid based on that
   * alone — track commit here and merge `appliedTabFilterParams` only after
   * `applyFilters()` runs.
   */
  private tabFiltersCommitted: Record<string, boolean> = {};
  private appliedTabFilterParams: Record<string, Record<string, string>> = {};

  /** Row count from the last `gridDataLoaded` for each step (for empty UI). */
  private gridRowCountByStateUrl: Record<string, number | undefined> = {};

  // Confirmation-modal state (for ctrlFn plans that require it, e.g.
  // `sub-sno-disable`, `sub-req-validations`). Mirrors
  // EfileElementsGridTplComponent.
  showConfirmModal = false;
  confirmModalConfig: ConfirmationModalConfig = {
    title: 'Confirm Action',
    message: 'Are you sure you want to proceed?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orangeDark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
  };
  private pendingPlan: CtrlFnPlan | null = null;
  private pendingGrid: EfileGridDirective | null = null;

  // Ignore overlay state (inline textarea popup; mirrors ignore-modal-tpl.html
  // from the legacy flow, kept inline so we don't require a second custom
  // element round-trip).
  showIgnoreOverlay = false;
  ignoreExplanation = '';
  ignoreSubmitting = false;

  /** Guard ported from EfileWorkflowController.js L8: only allow users whose
   * sso_id is in `SERVER_CONFIG.configSettings.enable_review_process`
   * (or the wildcard `"all"`). */
  reviewProcessEnabled = false;

  constructor(
    private cdr: ChangeDetectorRef,
    private workflowService: EfileWorkflowService,
    private globalService: GlobalService,
    private appService: AppService,
    private submissionService: SubmissionService,
    private snackbar: GtwSnackbarService,
    private ctrlFnDispatcher: EfileCtrlFnDispatcherService,
    private router: Router,
    private route: ActivatedRoute,
    private workspaceService: WorkspaceService,
  ) {}

  /**
   * Marker we attach to the synthetic `activeScreen.filters` envelope we
   * register on behalf of return-review tab filters. Lets us recognise our
   * own object when the user tabs away or this component is destroyed so we
   * don't blow away a real `FilterObj` that something else may have put in
   * its place.
   */
  private static readonly ACTIVE_SCREEN_FILTERS_TAG = '__efileReturnReviewSyntheticFilters';

  /** The `activeScreen` we last attached our synthetic filters to. Tracked
   *  so we can detach cleanly when the active screen changes or the
   *  component is destroyed. */
  private filtersAttachedToScreen: any = null;

  ngOnInit(): void {
    this.reviewProcessEnabled = this.computeReviewProcessFlag();
    if (!this.reviewProcessEnabled) {
      return;
    }
    this.loadSteps();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['submission'] && !changes['submission'].firstChange) {
      this.gridConfigByStateUrl = {};
      this.filtersByStateUrl = {};
      this.filtersLoadingByStateUrl = {};
      this.filtersLoadedByStateUrl = {};
      this.tabFiltersCommitted = {};
      this.appliedTabFilterParams = {};
      this.gridRowCountByStateUrl = {};
      this.selectedStateUrl = null;
      this.isDashboardSelected = true;
      this.workflowService.activeWorkflowStepId = null;
      this.exporting = false;
      this.dashboardIrsFilter = null;
      this.dashboardIrsFilterLoading = false;
      this.dashboardIrsFilterLoaded = false;
      this.dashboardIrsUserPicked = false;
      // Clear any synthetic filters envelope keyed off the prior submission
      // so the next `loadSteps()` reinstalls a fresh one tied to the new
      // submission's id / tax_year / scenario.
      this.detachActiveScreenFilters();
      if (this.reviewProcessEnabled) {
        this.loadSteps();
      }
    }

    // The input may arrive before steps load (deep-link case) or after the
    // user clicks a different sidebar URL (later). Either way: defer to
    // `applyChildSubState` which is a no-op if `steps` isn't populated yet —
    // `loadSteps()` will replay it once the response lands.
    if (changes['childSubState'] && !changes['childSubState'].firstChange) {
      this.applyChildSubState(this.childSubState);
    }
  }

  ngOnDestroy(): void {
    this.detachActiveScreenFilters();
    this.workflowService.activeWorkflowStepId = null;
    this.subs.unsubscribe();
  }

  /**
   * Register a synthetic `activeScreen.filters` envelope that exposes a
   * legacy-shaped `getFilterParams()` reading the live `filtersByStateUrl`
   * for the currently selected step plus the active submission's
   * `submission_id` / `tax_year` / `scenario_id`.
   *
   * Why this exists:
   *   - Legacy `EfileCustomGrid` (EfileWorkflowController.js L271) and
   *     `EfileDashboardController` (EfileElementsController.js L1003-L1013)
   *     both write tab-level filter params onto
   *     `workspaceFactory.activeScreen.filters` so other parts of the shell
   *     (data-filters action buttons, modals opened via
   *     `launchCustomModalForSpecificScreen`, ctrlFn dispatchers) can read
   *     `activeScreen.filters.getFilterParams()` and inherit the user's
   *     selection.
   *   - The migrated tab-level filters in `RETURN_REVIEW_GRID_CONFIGS`
   *     (e.g. `xml-vs-elements` IRS Submissions filter, efile.js
   *     L1005-L1033) live only in `filtersByStateUrl` — `data-filters`
   *     never sees them, so the **Extract Elements from XML** button
   *     opens its modal with `filterParams = {}` and the migrated
   *     `XmlVsElementsComponent` reports `irs_id` null.
   *
   * The envelope is intentionally dynamic: `getFilterParams()` rebuilds
   * the param map from the *current* `filtersByStateUrl[selectedStateUrl]`
   * each call, so it reflects post-selection state without us having to
   * mirror every dropdown change. This matches legacy `DataFiltersService`
   * (DataFiltersService.js L336-L420), where `getFilterParams()` walks
   * `this.filters[*].selected` live.
   */
  private syncTabFiltersToActiveScreen(): void {
    const active = this.workspaceService.workspaceFactory?.activeScreen;
    if (!active) {
      return;
    }

    const existing = active.filters;
    const isOurEnvelope =
      existing && (existing as any)[EfileReturnReviewComponent.ACTIVE_SCREEN_FILTERS_TAG] === true;
    const isEmptyStub =
      existing && (!Array.isArray(existing.filters) || existing.filters.length === 0)
        && existing.loaded === true;

    // Only replace the screen's filters object when it's empty (the synthetic
    // stub data-filters.component.ts:initFilters builds for screens with no
    // filter_key) or is already our envelope. Never replace a real FilterObj
    // — that would silently break other consumers.
    if (existing && !isOurEnvelope && !isEmptyStub) {
      return;
    }

    const self = this;
    const getCurrentParams = (): Record<string, any> => {
      const params: Record<string, any> = {};
      const sub = self.activeSubmission();
      if (sub?.SUBMISSION_ID != null) {
        params.submission_id = sub.SUBMISSION_ID;
      }
      if (sub?.TAX_YEAR != null) {
        params.tax_year = sub.TAX_YEAR;
      }
      const url = self.selectedStateUrl;
      if (url && self.filtersByStateUrl[url]) {
        for (const f of self.filtersByStateUrl[url]) {
          const paramName = (f as any).param_name as string | undefined;
          const selected = Array.isArray(f.selected) ? f.selected : [];
          if (!paramName || selected.length === 0) {
            continue;
          }
          const values = selected
            .map((s: any) => (s?.VALUE !== undefined ? String(s.VALUE) : ''))
            .filter(Boolean);
          if (values.length > 0) {
            params[paramName] = values.join(',');
          }
        }
      }
      return params;
    };

    const envelope: any = {
      [EfileReturnReviewComponent.ACTIVE_SCREEN_FILTERS_TAG]: true,
      loaded: true,
      filters: [],
      get filterParams() {
        return getCurrentParams();
      },
      getFilterParams: () => getCurrentParams(),
      getFilterParamsFromCache: () => getCurrentParams(),
      getFilters: () => {
        const url = self.selectedStateUrl;
        return (url && self.filtersByStateUrl[url]) || [];
      },
      setLoaded: (_b: boolean) => { /* no-op: always loaded */ },
      toggleFilters: () => { /* no-op: tab-level filters are inline */ },
    };

    active.filters = envelope;
    this.filtersAttachedToScreen = active;
  }

  /**
   * Remove our synthetic envelope when leaving the screen / destroying the
   * component, so any future tab activation can install its own
   * `FilterObj` cleanly. Only detaches our own envelope — never a real
   * `FilterObj` someone else might have installed in between.
   */
  private detachActiveScreenFilters(): void {
    const screen = this.filtersAttachedToScreen;
    this.filtersAttachedToScreen = null;
    if (!screen?.filters) {
      return;
    }
    if ((screen.filters as any)[EfileReturnReviewComponent.ACTIVE_SCREEN_FILTERS_TAG] === true) {
      screen.filters = null;
    }
  }

  /**
   * Match the URL-driven leaf (e.g. `efile-diagnostics`, `errors`) against
   * the loaded steps' STATE_URLs and select that step. The URL slug is
   * normalised through `URL_SLUG_TO_STATE_LEAF` first so the legacy slugs
   * `errors` and `efile-sub-scrubbings` resolve to the `validation` /
   * `scrubbings` state-name leaves the backend returns. Bail out silently
   * when no match — the default first-step selection from `loadSteps()`
   * stays intact.
   */
  private applyChildSubState(raw: string): void {
    const slug = (raw || '').trim().toLowerCase();

    // Empty slug or explicit 'dashboard' → activate the static Dashboard tab.
    // Mirrors the legacy redirect from `/return-review` → `/return-review/dashboard`
    // in EfileWorkflowController.js L17-26.
    if (!slug || slug === 'dashboard') {
      this.isDashboardSelected = true;
      this.selectedStateUrl = null;
      this.showIgnoreButton = false;
      this.workflowService.activeWorkflowStepId = null;
      this.ensureDashboardIrsFilterLoaded();
      this.cdr.markForCheck();
      return;
    }

    if (this.steps.length === 0) return;
    this.isDashboardSelected = false;
    const targetLeaf = this.normalizeLeaf(URL_SLUG_TO_STATE_LEAF[slug] || slug);
    const match = this.steps.find(
      (s) => this.normalizeLeaf(this.resolveLeafKey(s.STATE_URL || '')) === targetLeaf,
    );
    if (!match) return;
    this.selectedStateUrl = match.STATE_URL;
    this.showIgnoreButton = match.STEP_STATUS === 'FAILED';
    this.workflowService.activeWorkflowStepId = match.WORKFLOW_STEP_ID ?? null;
    this.ensureFiltersLoaded(match.STATE_URL);
    this.syncTabFiltersToActiveScreen();
    this.cdr.markForCheck();
  }

  // ---------------------------------------------------------------------------
  // Data loading
  // ---------------------------------------------------------------------------

  /**
   * @param opts.silent When true, skip toggling the `loading` flag so the
   *   template does NOT swap the main content for `#loadingTpl`. The visible
   *   step host (and its child `<gtw-efile-validation-errors>` / grid)
   *   therefore stays mounted across the refetch, which prevents downstream
   *   side-effects such as the validation card's `ngOnInit` re-firing
   *   `loadGridJson?grid_id=5779&action_code=9cv61f`. Use this for in-flow
   *   reloads where we only need the tab-strip statuses to refresh — e.g.
   *   right after `submitIgnoreOverlay()` posts `q05d2n` — and the user
   *   should not see a full-screen "Loading workflow steps…" flash.
   */
  loadSteps(opts: { silent?: boolean; dashboardRefresh?: boolean } = {}): void {
    const submissionId = this.activeSubmission()?.SUBMISSION_ID;
    if (!submissionId) {
      this.loadError = 'No active submission selected';
      this.cdr.markForCheck();
      return;
    }

    const baseUrl = generalConfigFromGlobal(this.globalService).base_url || '/gtw';

    const silent = !!opts.silent;
    const dashboardRefresh = !!opts.dashboardRefresh;
    if (dashboardRefresh) {
      this.dashboardRefreshing = true;
    }
    if (!silent) {
      this.loading = true;
    }
    this.loadError = null;
    this.cdr.markForCheck();

    this.subs.add(
      this.workflowService.loadSteps(baseUrl, submissionId, this.globalService, {
        fresh: silent || dashboardRefresh,
      }).subscribe({
        next: (response) => {
          this.steps = Array.isArray(response?.jsonObject) ? response.jsonObject : [];
          this.rebuildGridConfigs();
          if (dashboardRefresh) {
            this.dashboardRefreshing = false;
            this.snackbar.openSnackBar(
              'Return Review dashboard refreshed.',
              'gtw-snackbar--green',
            );
          }
          // Keep the Ignore-button visibility honest after a silent refresh:
          // if the currently selected step's status flipped (e.g. FAILED →
          // IGNORED right after `submitIgnoreOverlay()` posts q05d2n) the
          // button should disappear immediately without waiting for the user
          // to click another tab. `selectStep()` is the only other writer of
          // this flag, so we re-derive it from the new `steps` array here.
          if (this.selectedStateUrl) {
            const active = this.steps.find(
              (s) => s.STATE_URL === this.selectedStateUrl,
            );
            this.showIgnoreButton = active?.STEP_STATUS === 'FAILED';
            this.workflowService.activeWorkflowStepId = active?.WORKFLOW_STEP_ID ?? null;
          }
          // Replay the deep-link slug (if any) — handles the case where the
          // URL arrived before the steps API resolved.
          this.applyChildSubState(this.childSubState);
          // Kick off the filter loader for whatever step is now active so
          // tab-level dropdowns (e.g. xml-vs-elements' IRS Submissions list)
          // hydrate alongside the grid host instead of waiting for a manual
          // tab click.
          if (this.selectedStateUrl) {
            this.ensureFiltersLoaded(this.selectedStateUrl);
          }
          if (this.isDashboardSelected) {
            this.ensureDashboardIrsFilterLoaded();
          }
          // Install the synthetic `activeScreen.filters` envelope right
          // after the active step is known. Doing it here (not waiting for
          // a Filter click) guarantees `data-filters` action buttons like
          // **Extract Elements from XML** can read the user's tab-level
          // pick (e.g. `irs_id`) the moment it changes — same UX parity as
          // legacy where `getFilterParams()` always reflected the live
          // `vm.filters[*].selected` array.
          this.syncTabFiltersToActiveScreen();
          this.syncUrlToSelectedStep();
          if (!silent) {
            this.loading = false;
          }
          this.cdr.markForCheck();
        },
        error: () => {
          this.dashboardRefreshing = false;
          if (!silent) {
            this.loading = false;
          }
          this.loadError = dashboardRefresh
            ? 'Failed to refresh Return Review dashboard'
            : 'Failed to load workflow steps';
          if (dashboardRefresh) {
            this.snackbar.openSnackBar(this.loadError, 'gtw-snackbar--red');
          }
          this.cdr.markForCheck();
        },
      }),
    );
  }

  // ---------------------------------------------------------------------------
  // Tab interactions
  // ---------------------------------------------------------------------------

  /**
   * Mirrors the legacy `ui-sref="{{step.STATE_URL}}"` directive on each
   * tab in `return-review.html`: clicking a tab triggers an actual route
   * change so the browser URL, breadcrumb, and back/forward stack all
   * stay synchronized with the active step.
   *
   * The route layout is `submissions-dasboard/submission/:id/return-review/:childLeaf`.
   * Once the navigation lands, `SubmissionDataComponent.capturePendingDeepLink`
   * forwards the new `:childLeaf` segment back into `[childSubState]`, which
   * in turn lights up `applyChildSubState()` here and swaps the visible
   * grid host. We also flip `selectedStateUrl` synchronously so the grid
   * swap is instant — the inbound `[childSubState]` update afterwards is
   * idempotent.
   */
  selectStep(step: ReturnReviewStep): void {
    this.isDashboardSelected = false;
    this.selectedStateUrl = step.STATE_URL;
    this.showIgnoreButton = step.STEP_STATUS === 'FAILED';
    this.workflowService.activeWorkflowStepId = step.WORKFLOW_STEP_ID ?? null;
    this.ensureFiltersLoaded(step.STATE_URL);
    this.syncTabFiltersToActiveScreen();
    this.cdr.markForCheck();

    const leaf = this.normalizeLeaf(this.resolveLeafKey(step.STATE_URL || ''));
    const submissionId = this.activeSubmission()?.SUBMISSION_ID;
    if (!leaf || submissionId == null) {
      // Fallback: keep the local state swap (already applied above) and
      // skip URL navigation if we can't compute the target — the visible
      // grid still updates so the user isn't stuck.
      return;
    }
    // Translate the state-name leaf to the legacy URL slug so the
    // address bar matches what ui-router used to render
    // (efile.js L621-L622, L913-L914). Other steps use their leaf
    // verbatim because the legacy `url:` and state-name suffix were
    // already identical (e.g. `efile-diagnostics`).
    const urlSlug = STATE_LEAF_TO_URL_SLUG[leaf] || leaf;
    this.router.navigate(
      ['/submissions-dasboard', 'submission', submissionId, 'return-review', urlSlug],
      {
        queryParamsHandling: 'preserve',
        // Replace the URL when the slug is the same — defensive no-op
        // guard so back/forward isn't polluted by self-navigations.
        replaceUrl: this.route.snapshot.params['subState'] === urlSlug,
      },
    );
  }

  /**
   * True when the currently selected step is the `validation` (legacy
   * `/errors`) workflow step — drives whether the content area renders
   * `<gtw-efile-validation-errors>` instead of a grid.
   */
  isValidationStep(step: ReturnReviewStep | null | undefined): boolean {
    if (!step) return false;
    const leaf = this.normalizeLeaf(this.resolveLeafKey(step.STATE_URL || ''));
    return leaf === 'validation';
  }

  isCurrentStepValidation(): boolean {
    return this.isValidationStep(this.currentStep());
  }

  /**
   * Re-fetch the workflow steps after the validation card emits a status
   * update. Runs in silent mode (no full-screen loader) so the validation
   * card stays mounted across the refetch — otherwise its `ngOnInit` would
   * re-fire `loadGridJson?grid_id=5779&action_code=9cv61f`, which is
   * exactly what the card just told us happened, creating a ping-pong.
   */
  onStepsReload(): void {
    this.loadSteps({ silent: true });
  }

  isSelected(step: ReturnReviewStep): boolean {
    return step.STATE_URL === this.selectedStateUrl;
  }

  statusClass(step: ReturnReviewStep): string {
    return (step.STEP_STATUS || '').toLowerCase();
  }

  statusIcon(step: ReturnReviewStep): string {
    switch (step.STEP_STATUS) {
      case 'PENDING':
        return 'fa fa-circle-o';
      case 'RESOLVED':
      case 'IGNORED':
        return 'fa fa-check-circle';
      case 'FAILED':
        return 'fa fa-exclamation-circle';
      default:
        return 'fa fa-circle-o';
    }
  }

  tooltipForStep(step: ReturnReviewStep): string {
    if (step.STEP_STATUS !== 'IGNORED') return '';
    const explanation = step.EXPLANATION || 'n/a';
    const ignoredBy = step.IGNORED_BY || 'n/a';
    return `Resolved with explanation: '${explanation}' by ${ignoredBy}`;
  }

  shortStepLabel(step: ReturnReviewStep): string {
    return step.STEP_NAME || '';
  }

  trackByStep(_index: number, step: ReturnReviewStep): string {
    return `${step.WORKFLOW_STEP_ID}`;
  }

  /**
   * trackBy used by the single-element `*ngFor="let key of [selectedStateUrl]"`
   * in the template — keying on the value forces Angular to destroy + recreate
   * the embedded view whenever the user switches steps, which in turn remounts
   * `<div efileGrid>` so gizmoGrid initialises against the live (visible)
   * container instead of a previously-hidden one.
   */
  trackByKey(_index: number, key: string | null): string {
    return key || '';
  }

  currentStep(): ReturnReviewStep {
    const match = this.steps.find((s) => s.STATE_URL === this.selectedStateUrl);
    return match || ({} as ReturnReviewStep);
  }

  // ---------------------------------------------------------------------------
  // Dashboard tab
  // ---------------------------------------------------------------------------

  /**
   * Activates the static Dashboard tab and navigates to `/return-review/dashboard`.
   * Mirrors the `ui-sref="…return-review.dashboard"` anchor in the legacy
   * `return-review.html` template.
   */
  /**
   * Reload workflow steps when the dashboard tab is opened or refreshed so
   * the consolidated table reflects the latest review activity.
   */
  refreshDashboard(): void {
    if (!this.reviewProcessEnabled) return;
    this.loadSteps({ silent: true, dashboardRefresh: true });
  }

  /** Navigate from a dashboard row to the matching Return Review workflow tab. */
  openDashboardRow(step: ReturnReviewStep): void {
    if (!step?.STATE_URL) return;
    this.selectStep(step);
  }

  selectDashboard(): void {
    this.isDashboardSelected = true;
    this.selectedStateUrl = null;
    this.showIgnoreButton = false;
    this.workflowService.activeWorkflowStepId = null;
    this.ensureDashboardIrsFilterLoaded();
    if (this.steps.length > 0) {
      this.loadSteps({ silent: true });
    }
    this.cdr.markForCheck();

    const submissionId = this.activeSubmission()?.SUBMISSION_ID;
    if (submissionId != null) {
      this.router.navigate(
        ['/submissions-dasboard', 'submission', submissionId, 'return-review', 'dashboard'],
        {
          queryParamsHandling: 'preserve',
          replaceUrl: this.route.snapshot.params['subState'] === 'dashboard',
        },
      );
    }
  }

  /**
   * Exports the Return Review data to Excel.
   * Mirrors `EfileReturnReviewController.exportReturnReview` in
   * `EfileWorkflowController.js` L90-175 and `EfileFactory.exportReturnReview`
   * in `EfileService.js` L574-590.
   *
   * Flow:
   *   1. POST /multiGridExportToExcel → receive { callSuccess, fileName|url }
   *   2a. If response has `url` → open directly.
   *   2b. If response has `fileName` → GET /getAPIAuthToken → open /download.
   */
  exportReturnReview(): void {
    if (this.exporting) return;
    this.exporting = true;
    this.cdr.markForCheck();

    const submission = this.activeSubmission();
    const activeScreen = (this.workspaceService as any).workspaceFactory?.activeScreen;
    const ssoId: string = this.appService.USER_SETTINGS?.user?.sso_id || '';
    const baseUrl = generalConfigFromGlobal(this.globalService).base_url || '/gtw';
    const gp: any = this.globalService.global?.globalParams || {};

    const selectedTaxYear = String(submission?.TAX_YEAR || '');
    const taxYearValues: any[] = (this.globalService as any).global?.inputs?.tax_year?.values || [];
    const taxYearEntry = taxYearValues.find((v: any) => String(v.value) === selectedTaxYear);
    const scenario = taxYearEntry ? String(taxYearEntry.scenario) : '';

    const exportPayload: Record<string, string> = {
      isFileDownloadRequest: 'Y',
      tax_year: selectedTaxYear,
      screen_key: String(activeScreen?.screen_key || '11212'),
      scenario,
      scenario_v1: scenario,
      jcd_key: String(gp.jcd_key || 250),
      jcd_ta_key: String(activeScreen?.jcd_ta_key || gp.jcd_ta_key || '5435'),
      activity_key: String(activeScreen?.activity_key || gp.activity_key || '7530'),
      screen_type: 'custom',
      is_locked: 'N',
      scenarioCode: gp.scenarioCode || '',
      is_issue_key_enabled: gp.is_issue_key_enabled || 'N',
      scenario_desc: (this.globalService as any).selected_scenario_desc || '',
      type: 'federal',
      submission_id: String(submission?.SUBMISSION_ID || ''),
      scenario_id: scenario,
    };

    const irsId = this.selectedDashboardIrsId();
    if (irsId) {
      exportPayload.irs_id = irsId;
    }

    const jsonObj = JSON.stringify(exportPayload);

    const headerArray = JSON.stringify([...RETURN_REVIEW_DASHBOARD_EXPORT_HEADERS]);

    this.subs.add(
      this.workflowService.exportReturnReview(baseUrl, ssoId, jsonObj, headerArray).subscribe({
        next: (data: any) => {
          if (data && String(data.callSuccess) === '1') {
            const fileName: string = data.fileName || data.file_name || '';
            if (data.url && String(data.url).length > 0) {
              this.exporting = false;
              this.cdr.markForCheck();
              window.open(data.url, '_blank');
            } else if (fileName.length > 0) {
              this.subs.add(
                this.workflowService.getApiAuthToken(baseUrl).subscribe({
                  next: (res: any) => {
                    this.exporting = false;
                    this.cdr.markForCheck();
                    if (res?.apiToken) {
                      const clientKey = sessionStorage.getItem('client_key') || '';
                      const downloadURL =
                        `${baseUrl}/download?file_name=${fileName}` +
                        `&client_key=${clientKey}` +
                        `&apiToken=${encodeURIComponent(res.apiToken)}` +
                        `&georaclehrid=${ssoId}`;
                      window.open(downloadURL, '_blank');
                    } else {
                      this.snackbar.openSnackBar('Failed to get download token.', 'gtw-snackbar--red');
                    }
                  },
                  error: () => {
                    this.exporting = false;
                    this.cdr.markForCheck();
                    this.snackbar.openSnackBar('Error occurred fetching download token.', 'gtw-snackbar--red');
                  },
                }),
              );
            } else {
              this.exporting = false;
              this.cdr.markForCheck();
              this.snackbar.openSnackBar('No data available to export.', 'gtw-snackbar--yellow');
            }
          } else {
            this.exporting = false;
            this.cdr.markForCheck();
            const errMsg: string =
              data?.errorMessage || 'Export failed. Please try again or contact support.';
            this.snackbar.openSnackBar(errMsg, 'gtw-snackbar--red');
          }
        },
        error: () => {
          this.exporting = false;
          this.cdr.markForCheck();
          this.snackbar.openSnackBar('Error occurred during export. Please contact support.', 'gtw-snackbar--red');
        },
      }),
    );
  }

  // ---------------------------------------------------------------------------
  // Ignore flow
  // ---------------------------------------------------------------------------

  openIgnoreOverlay(): void {
    // Mirrors EfileWorkflowController.js L32-35: block when the submission
    // is already in production-accepted state.
    const submission = this.activeSubmission();
    if ((submission as any)?.SUBMISSION_STATUS === 'Production Accepted') {
      this.snackbar.openSnackBar(
        'You cannot perform this action on a submission that is already submitted in production.',
        'gtw-snackbar--yellow',
      );
      return;
    }
    this.ignoreExplanation = '';
    this.showIgnoreOverlay = true;
    this.cdr.markForCheck();
  }

  cancelIgnoreOverlay(): void {
    this.showIgnoreOverlay = false;
    this.ignoreExplanation = '';
    this.cdr.markForCheck();
  }

  onIgnoreExplanationChange(value: string): void {
    this.ignoreExplanation = value;
    this.cdr.markForCheck();
  }

  submitIgnoreOverlay(): void {
    const explanation = (this.ignoreExplanation || '').trim();
    if (!explanation || this.ignoreSubmitting) return;

    const submission = this.activeSubmission();
    const currentStep = this.steps.find((s) => s.STATE_URL === this.selectedStateUrl);
    if (!submission?.SUBMISSION_ID || !currentStep) {
      this.snackbar.openSnackBar('Unable to ignore step: missing context', 'gtw-snackbar--red');
      return;
    }

    const gp = this.globalService.global?.globalParams || {};
    const baseUrl = generalConfigFromGlobal(this.globalService).base_url || '/gtw';
    const ssoId = this.appService.USER_SETTINGS?.user?.sso_id || '';

    this.ignoreSubmitting = true;
    this.cdr.markForCheck();

    this.subs.add(
      this.workflowService
        .ignoreStep(baseUrl, {
          sso_id: ssoId,
          submission_id: submission.SUBMISSION_ID,
          step_id: currentStep.WORKFLOW_STEP_ID,
          step_status: 'IGNORED',
          explanation,
          tax_year: submission.TAX_YEAR ?? gp.tax_year,
          scenario: gp.scenario,
          jcd_key: 250, // legacy pin (EfileWorkflowController.js L92)
        })
        .subscribe({
          next: (response: any) => {
            this.ignoreSubmitting = false;
            if (String(response?.callSuccess) === '1') {
              this.snackbar.openSnackBar('Step ignored successfully', 'gtw-snackbar--green');
              this.showIgnoreOverlay = false;
              this.ignoreExplanation = '';
              // Silent reload: refreshes the tab-strip statuses (so the
              // newly-IGNORED step picks up the right icon + tooltip and
              // the Ignore button disappears for that step) WITHOUT
              // toggling `this.loading`. That keeps the currently-visible
              // step host mounted, so `<gtw-efile-validation-errors>` is
              // not destroyed and re-created — which is what previously
              // re-fired `loadGridJson?grid_id=5779&action_code=9cv61f`
              // immediately after the ignore-save. Legacy parity: the
              // AngularJS controller likewise only updated `vm.steps` in
              // place, never reloading the inner state's grid.
              this.loadSteps({ silent: true });
            } else {
              this.snackbar.openSnackBar(
                response?.errorMessage || 'Failed to ignore step',
                'gtw-snackbar--red',
              );
              this.cdr.markForCheck();
            }
          },
          error: () => {
            this.ignoreSubmitting = false;
            this.snackbar.openSnackBar('Failed to ignore step', 'gtw-snackbar--red');
            this.cdr.markForCheck();
          },
        }),
    );
  }

  // ---------------------------------------------------------------------------
  // Internals
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // Per-step grid config (Phase 2 wiring)
  // ---------------------------------------------------------------------------

  /**
   * Returns the grid config to mount for a given step, or `null` if the
   * step has no grid equivalent (e.g. `validation`/`errors`, which is a
   * form-based screen in legacy code).
   */
  gridConfigFor(step: ReturnReviewStep): EfileGridConfig | null {
    if (!step?.STATE_URL) return null;
    return this.gridConfigByStateUrl[step.STATE_URL] ?? null;
  }

  currentGridConfig(): EfileGridConfig | null {
    if (!this.selectedStateUrl) return null;
    return this.gridConfigByStateUrl[this.selectedStateUrl] ?? null;
  }

  /**
   * GTW diagnostics (`efile-diagnostics`, grid 5712) relies on gizmoGrid
   * drilldown / link glyphs (e.g. Diagnostics Failed Count). Other return-review
   * grids hide those controls via scoped CSS — see `.step-grid-host--efile-diagnostics`.
   */
  isEfileDiagnosticsGridStep(): boolean {
    if (!this.selectedStateUrl) {
      return false;
    }
    const leaf = this.normalizeLeaf(this.resolveLeafKey(this.selectedStateUrl));
    return leaf === 'efile-diagnostics';
  }

  /** Resolve the leaf key (e.g. `efile-diagnostics`) from either a full
   *  legacy state URL or a bare leaf. */
  private resolveLeafKey(stateUrl: string): string | null {
    if (!stateUrl) return null;
    const lastDot = stateUrl.lastIndexOf('.');
    const leaf = lastDot >= 0 ? stateUrl.substring(lastDot + 1) : stateUrl;
    const normalized = (leaf || '').trim().toLowerCase();
    if (!normalized) return null;
    return normalized;
  }

  private normalizeLeaf(leaf: string | null): string {
    const key = (leaf || '').trim().toLowerCase();
    if (!key) return '';
    return RETURN_REVIEW_LEAF_ALIASES[key] || key;
  }

  private syncUrlToSelectedStep(): void {
    const submissionId = this.activeSubmission()?.SUBMISSION_ID;
    if (submissionId == null) return;

    if (this.isDashboardSelected) {
      if ((this.childSubState || '').trim().length > 0) return;
      this.router.navigate(
        ['/submissions-dasboard', 'submission', submissionId, 'return-review', 'dashboard'],
        { queryParamsHandling: 'preserve', replaceUrl: true },
      );
      return;
    }

    const selected = this.currentStep();
    if (!selected?.STATE_URL) return;
    if ((this.childSubState || '').trim().length > 0) return;

    const leaf = this.normalizeLeaf(this.resolveLeafKey(selected.STATE_URL));
    if (!leaf) return;
    const urlSlug = STATE_LEAF_TO_URL_SLUG[leaf] || leaf;
    this.router.navigate(
      ['/submissions-dasboard', 'submission', submissionId, 'return-review', urlSlug],
      {
        queryParamsHandling: 'preserve',
        replaceUrl: true,
      },
    );
  }

  private rebuildGridConfigs(): void {
    this.gridConfigByStateUrl = {};
    this.tabFiltersCommitted = {};
    this.appliedTabFilterParams = {};
    this.gridRowCountByStateUrl = {};
    const submission = this.activeSubmission();
    if (!submission?.SUBMISSION_ID) return;

    for (const step of this.steps) {
      const config = this.buildGridConfig(step.STATE_URL, submission);
      if (config) {
        this.gridConfigByStateUrl[step.STATE_URL] = config;
      }
    }
  }

  private buildGridConfig(stateUrl: string, submission: Submission): EfileGridConfig | null {
    const leaf = this.normalizeLeaf(this.resolveLeafKey(stateUrl));
    if (!leaf) return null;

    const base = RETURN_REVIEW_GRID_CONFIGS[leaf];
    if (!base) return null;

    const ssoId = this.appService.USER_SETTINGS?.user?.sso_id || '';
    const gp = this.globalService.global?.globalParams || {};
    const extras: Record<string, any> = {
      submission_id: String(submission.SUBMISSION_ID),
    };

    // Legacy `EfileCustomGrid` `all_filter_data` always includes scenario_id
    // derived from the submission tax year (EfileWorkflowController.js L146-147).
    const taxYearValues: any[] =
      (this.globalService as any).global?.inputs?.tax_year?.values || [];
    const taxYearEntry = taxYearValues.find(
      (v: any) => String(v.value) === String(submission.TAX_YEAR),
    );
    if (taxYearEntry?.scenario != null && String(taxYearEntry.scenario).trim() !== '') {
      extras.scenario_id = String(taxYearEntry.scenario);
    }

    for (const key of base.extraParamKeys || []) {
      if (key === 'sso_id') extras.sso_id = ssoId;
      else if (key === 'scenario_id') {
        extras.scenario_id =
          (gp as any).scenario ?? (submission as any).SCENARIO_ID ?? (submission as any).SCENARIO ?? '';
      }
    }

    // Tab-level filter query params (`irs_id`, …) are merged from
    // `appliedTabFilterParams` only after the user clicks **Filter**
    // (`applyFilters`). Dropdown changes alone must not change `extraParams`.
    const filterExtras = this.appliedTabFilterParams[stateUrl]
      ? { ...this.appliedTabFilterParams[stateUrl] }
      : {};
    Object.assign(extras, filterExtras);

    // Tab-level filters (xml-vs-elements*): no fetch until required picks are
    // made **and** the user clicks **Filter** (`tabFiltersCommitted`). Steps
    // without tab filters keep the old `!hasUnsatisfiedRequiredFilters` rule.
    const specFilters = base.filters || [];
    const hasTabLevelFilters = specFilters.length > 0;
    const unsatisfied = this.hasUnsatisfiedRequiredFilters(stateUrl, base);
    const autoLoad = hasTabLevelFilters
      ? !unsatisfied && !!this.tabFiltersCommitted[stateUrl]
      : !unsatisfied;

    return {
      gridId: base.gridId,
      actionId: base.actionId,
      actionCode: base.actionCode,
      extraParams: extras,
      autoLoad,
      cacheKey: `efile/return-review/${leaf}/${submission.SUBMISSION_ID}`,
      ...RETURN_REVIEW_LEGACY_EXPORT,
    };
  }

  /**
   * Reads the **current** tab-level filter UI (`filtersByStateUrl`) into
   * `paramName -> value` pairs. Used only when applying filters — not for
   * `buildGridConfig` until `applyFilters` copies into `appliedTabFilterParams`.
   */
  /** Selected `irs_id` for dashboard export; falls back to newest when unset. */
  selectedDashboardIrsId(): string {
    const filter = this.dashboardIrsFilter;
    if (!filter) return '';
    const selected = Array.isArray(filter.selected) ? filter.selected : [];
    const value = selected[0]?.VALUE;
    if (value !== undefined && value !== null && String(value).trim()) {
      return String(value).trim();
    }
    const latest = pickLatestIrsSubmissionOption(filter.values || []);
    return latest?.VALUE !== undefined && latest?.VALUE !== null
      ? String(latest.VALUE).trim()
      : '';
  }

  onDashboardIrsFilterChange(filter: DfSelectSingleFilter): void {
    const selected = Array.isArray(filter.selected) ? filter.selected : [];
    if (selected.length === 0) {
      const rows = filter.values || [];
      const latest = pickLatestIrsSubmissionOption(rows);
      if (latest && this.dashboardIrsFilter) {
        this.dashboardIrsFilter = {
          ...this.dashboardIrsFilter,
          selected: [latest],
        };
        this.dashboardIrsUserPicked = false;
      }
    } else {
      this.dashboardIrsUserPicked = true;
      if (this.dashboardIrsFilter) {
        this.dashboardIrsFilter = {
          ...this.dashboardIrsFilter,
          selected: [...selected],
        };
      }
    }
    this.cdr.markForCheck();
  }

  reloadDashboardIrsFilter(): void {
    this.dashboardIrsFilterLoaded = false;
    this.ensureDashboardIrsFilterLoaded(true);
  }

  private dashboardIrsIdToPreserve(): string {
    if (!this.dashboardIrsUserPicked || !this.dashboardIrsFilter) return '';
    const selected = Array.isArray(this.dashboardIrsFilter.selected)
      ? this.dashboardIrsFilter.selected
      : [];
    const value = selected[0]?.VALUE;
    return value !== undefined && value !== null ? String(value).trim() : '';
  }

  private buildDashboardIrsFilter(rows: DfSelectSingleOption[]): DfSelectSingleFilter {
    const preserveId = this.dashboardIrsIdToPreserve();
    const filter = this.makeEmptyFilter(DASHBOARD_IRS_FILTER_SPEC);
    return {
      ...filter,
      values: rows,
      selected: resolveDashboardIrsSelected(rows, preserveId),
      loading: false,
    };
  }

  /**
   * Loads IRS Submissions options for the dashboard export bar (`j4k0an`).
   * Mirrors the xml-vs-elements / xml-vs-elements-counts tab filter list.
   */
  private ensureDashboardIrsFilterLoaded(force = false): void {
    if (!this.isDashboardSelected) return;
    if (this.dashboardIrsFilterLoaded && !force) return;
    if (this.dashboardIrsFilterLoading && !force) return;

    const submission = this.activeSubmission();
    if (!submission?.SUBMISSION_ID) return;

    if (!this.dashboardIrsFilter) {
      this.dashboardIrsFilter = this.makeEmptyFilter(DASHBOARD_IRS_FILTER_SPEC);
    }

    const baseUrl = generalConfigFromGlobal(this.globalService).base_url || '/gtw';
    this.dashboardIrsFilterLoading = true;
    this.cdr.markForCheck();

    this.subs.add(
      this.workflowService
        .loadIrsSubmissions(baseUrl, submission.SUBMISSION_ID!, this.globalService, {
          fresh: force,
        })
        .subscribe({
          next: (res) => {
            const rows = (Array.isArray(res?.jsonObject) ? res.jsonObject : []) as DfSelectSingleOption[];
            this.dashboardIrsFilter = this.buildDashboardIrsFilter(rows);
            this.dashboardIrsFilterLoading = false;
            this.dashboardIrsFilterLoaded = true;
            this.cdr.markForCheck();
          },
          error: () => {
            if (this.dashboardIrsFilter) {
              this.dashboardIrsFilter = this.buildDashboardIrsFilter([]);
            }
            this.dashboardIrsFilterLoading = false;
            this.dashboardIrsFilterLoaded = true;
            this.cdr.markForCheck();
          },
        }),
    );
  }

  private collectFilterParamsFromSelections(stateUrl: string): Record<string, string> {
    const out: Record<string, string> = {};
    const filters = this.filtersByStateUrl[stateUrl];
    if (!filters?.length) return out;
    for (const f of filters) {
      const paramName = (f as any).param_name as string | undefined;
      const selected = Array.isArray(f.selected) ? f.selected : [];
      if (!paramName || selected.length === 0) continue;
      const values = selected
        .map((s) => (s?.VALUE !== undefined ? String(s.VALUE) : ''))
        .filter(Boolean);
      if (values.length > 0) {
        out[paramName] = values.join(',');
      }
    }
    return out;
  }

  /** Returns true when any required filter on this step has no selection. */
  private hasUnsatisfiedRequiredFilters(
    stateUrl: string,
    base: ReturnReviewGridBase,
  ): boolean {
    const specs = base.filters || [];
    if (specs.length === 0) return false;
    const filters = this.filtersByStateUrl[stateUrl] || [];
    for (const spec of specs) {
      if (!spec.required) continue;
      const f = filters.find((x) => (x as any).param_name === spec.paramName);
      const selected = (f?.selected as DfSelectSingleOption[]) || [];
      if (selected.length === 0) return true;
    }
    return false;
  }

  // ---------------------------------------------------------------------------
  // Tab-level filters (mirrors legacy EfileCustomGrid filters block)
  // ---------------------------------------------------------------------------

  /**
   * Look up the declarative filter specs for a given step. Used by the
   * template to decide whether to render a filter bar above the grid.
   */
  filterSpecsFor(stateUrl: string | null): ReturnReviewFilterSpec[] {
    if (!stateUrl) return [];
    const leaf = this.normalizeLeaf(this.resolveLeafKey(stateUrl));
    return RETURN_REVIEW_GRID_CONFIGS[leaf]?.filters || [];
  }

  /** Active filter list for a given step (template binding helper). */
  filtersFor(stateUrl: string | null): DfSelectSingleFilter[] {
    if (!stateUrl) return [];
    return this.filtersByStateUrl[stateUrl] || [];
  }

  /** Convenience accessor used by the template for the current step. */
  currentFilterSpecs(): ReturnReviewFilterSpec[] {
    return this.filterSpecsFor(this.selectedStateUrl);
  }

  currentFilters(): DfSelectSingleFilter[] {
    return this.filtersFor(this.selectedStateUrl);
  }

  /** Loading flag for the filter row (mirrors legacy `!ctrl.filtersLoaded`). */
  isFiltersLoading(stateUrl: string | null): boolean {
    if (!stateUrl) return false;
    return !!this.filtersLoadingByStateUrl[stateUrl];
  }

  isCurrentFiltersLoading(): boolean {
    return this.isFiltersLoading(this.selectedStateUrl);
  }

  /**
   * True while the grid body should stay hidden: missing required filter
   * picks, or the user changed a dropdown but has not clicked **Filter** yet.
   */
  isCurrentGridGatedByFilters(): boolean {
    if (!this.selectedStateUrl) return false;
    const leaf = this.normalizeLeaf(this.resolveLeafKey(this.selectedStateUrl));
    const base = RETURN_REVIEW_GRID_CONFIGS[leaf];
    if (!base?.filters?.length) return false;
    if (this.hasUnsatisfiedRequiredFilters(this.selectedStateUrl, base)) {
      return true;
    }
    return !this.tabFiltersCommitted[this.selectedStateUrl];
  }

  /**
   * Disable the green **Filter** button only when a required dropdown is still
   * empty — not while the grid is merely "uncommitted" after a valid pick.
   */
  isFilterButtonDisabled(): boolean {
    if (!this.selectedStateUrl) return true;
    const leaf = this.normalizeLeaf(this.resolveLeafKey(this.selectedStateUrl));
    const base = RETURN_REVIEW_GRID_CONFIGS[leaf];
    if (!base?.filters?.length) return false;
    return this.hasUnsatisfiedRequiredFilters(this.selectedStateUrl, base);
  }

  /** After a load, show an in-host empty state when the grid returned zero rows. */
  isReturnReviewGridEmpty(): boolean {
    if (!this.selectedStateUrl) return false;
    if (this.isCurrentGridGatedByFilters()) return false;
    const leaf = this.normalizeLeaf(this.resolveLeafKey(this.selectedStateUrl));
    if (!RETURN_REVIEW_GRID_CONFIGS[leaf]) return false;
    return this.gridRowCountByStateUrl[this.selectedStateUrl] === 0;
  }

  onReturnReviewGridDataLoaded(payload: any): void {
    const url = this.selectedStateUrl;
    if (!url) return;
    const rows = payload?.data ?? payload?.DATA;
    const n = Array.isArray(rows) ? rows.length : -1;
    this.gridRowCountByStateUrl[url] = n >= 0 ? n : undefined;
    this.cdr.markForCheck();
  }

  private refreshGridConfigForStateUrl(stateUrl: string): void {
    const submission = this.activeSubmission();
    if (!submission?.SUBMISSION_ID) return;
    const cfg = this.buildGridConfig(stateUrl, submission);
    if (cfg) {
      this.gridConfigByStateUrl[stateUrl] = cfg;
    }
  }

  /**
   * trackBy for the per-state filter `*ngFor`. Keys on `param_name` so
   * Angular can re-use existing `[df-select-single]` directive instances
   * when filters re-render after a value load.
   */
  trackByFilter(_index: number, filter: DfSelectSingleFilter): string {
    return ((filter as any).param_name as string) || _index.toString();
  }

  /**
   * Lazily fetches the option lists for any filters declared on the
   * given step (idempotent — guarded by `filtersLoadedByStateUrl`).
   * Mirrors `EfileCustomGrid.loadingFilters()` (EfileWorkflowController.js
   * L228-L248): each filter spec is loaded via
   * `JsonObjectFactoryService.getJSONObj` (already routed through
   * `EfileWorkflowService.loadIrsSubmissions` for the IRS list) and
   * the resulting `{LABEL, VALUE}` rows are written onto a
   * `DfSelectSingleFilter` instance per spec.
   */
  private ensureFiltersLoaded(stateUrl: string): void {
    const leaf = this.normalizeLeaf(this.resolveLeafKey(stateUrl));
    const specs = RETURN_REVIEW_GRID_CONFIGS[leaf]?.filters || [];
    if (specs.length === 0) return;

    if (!this.filtersByStateUrl[stateUrl]) {
      this.filtersByStateUrl[stateUrl] = specs.map((spec) =>
        this.makeEmptyFilter(spec),
      );
    }

    if (this.filtersLoadedByStateUrl[stateUrl] || this.filtersLoadingByStateUrl[stateUrl]) return;

    const submission = this.activeSubmission();
    if (!submission?.SUBMISSION_ID) return;

    const baseUrl = generalConfigFromGlobal(this.globalService).base_url || '/gtw';

    this.filtersLoadingByStateUrl[stateUrl] = true;
    this.cdr.markForCheck();

    const filters = this.filtersByStateUrl[stateUrl];
    let pending = specs.length;

    specs.forEach((spec, idx) => {
      // Only the IRS Submissions filter (`j4k0an`) is in scope today —
      // routed through the dedicated workflow service helper so the
      // legacy `globalParams + filterParams` merge happens behind the
      // scenes (see `EfileWorkflowService.loadIrsSubmissions`).
      const obs =
        spec.actionCode === 'j4k0an'
          ? this.workflowService.loadIrsSubmissions(
              baseUrl,
              submission.SUBMISSION_ID!,
              this.globalService,
            )
          : null;
      if (!obs) {
        pending -= 1;
        if (pending === 0) {
          this.finishFilterLoad(stateUrl);
        }
        return;
      }
      this.subs.add(
        obs.subscribe({
          next: (res) => {
            const rows = Array.isArray(res?.jsonObject) ? res.jsonObject : [];
            this.replaceFilter(stateUrl, idx, {
              values: rows as DfSelectSingleOption[],
              loading: false,
            });
            pending -= 1;
            if (pending === 0) {
              this.finishFilterLoad(stateUrl);
            } else {
              this.cdr.markForCheck();
            }
          },
          error: () => {
            this.replaceFilter(stateUrl, idx, {
              values: [],
              loading: false,
            });
            pending -= 1;
            if (pending === 0) {
              this.finishFilterLoad(stateUrl);
            } else {
              this.cdr.markForCheck();
            }
          },
        }),
      );
    });
  }

  /**
   * Replace a filter object at `[stateUrl][idx]` with a shallow-merged
   * clone. `df-select-single`'s `@Input` change detection only fires
   * when the bound reference itself changes — mutating
   * `filter.values` / `filter.loading` in place is silently ignored,
   * so the dropdown would otherwise stay stuck on its initial
   * spinner. Cloning forces ngOnChanges -> syncFromFilter ->
   * re-render on the directive instance.
   */
  private replaceFilter(
    stateUrl: string,
    idx: number,
    patch: Partial<DfSelectSingleFilter>,
  ): void {
    const list = this.filtersByStateUrl[stateUrl];
    if (!list || !list[idx]) return;
    const current = list[idx];
    const next: DfSelectSingleFilter = {
      ...current,
      ...patch,
      // Preserve the param_name / label_name / value_name extras we tag
      // onto the filter — `Partial<DfSelectSingleFilter>` doesn't carry
      // them on its public type but they live under `[key: string]`.
    };
    (next as any).param_name = (current as any).param_name;
    (next as any).label_name = (current as any).label_name;
    (next as any).value_name = (current as any).value_name;
    list[idx] = next;
  }

  private finishFilterLoad(stateUrl: string): void {
    this.filtersLoadingByStateUrl[stateUrl] = false;
    this.filtersLoadedByStateUrl[stateUrl] = true;
    this.cdr.markForCheck();
  }

  private makeEmptyFilter(spec: ReturnReviewFilterSpec): DfSelectSingleFilter {
    const filter: DfSelectSingleFilter = {
      label: spec.label,
      loading: true,
      values: [],
      selected: [],
    };
    (filter as any).param_name = spec.paramName;
    (filter as any).label_name = 'LABEL';
    (filter as any).value_name = 'VALUE';
    return filter;
  }

  /**
   * Filter-row callback wired in the template — fires whenever the
   * `[df-select-single]` directive emits `(filterChange)`. The
   * directive mutates `selected` in place but the OnPush change
   * detector won't see the assignment without an explicit poke.
   */
  onFilterValueChange(_filter: DfSelectSingleFilter): void {
    const url = this.selectedStateUrl;
    if (url) {
      this.tabFiltersCommitted[url] = false;
      delete this.appliedTabFilterParams[url];
      delete this.gridRowCountByStateUrl[url];
      this.refreshGridConfigForStateUrl(url);
    }
    // Make sure the synthetic `activeScreen.filters` envelope is in place.
    // `getFilterParams()` reads `filtersByStateUrl` live so we don't need to
    // mirror the value, but we DO need the envelope itself attached so any
    // downstream consumer (e.g. data-filters' "Extract Elements from XML"
    // button → `launchCustomModalForSpecificScreen`) sees the user's
    // current pick the moment they click — same UX as legacy where
    // `getFilterParams()` always reflected `vm.filters[*].selected`.
    this.syncTabFiltersToActiveScreen();
    this.cdr.markForCheck();
  }

  /**
   * Filter button handler — mirrors `<div class="btn ...Filter">` +
   * `ng-click="ctrl.refresh()"` in legacy `grid-tpl.html`. We rebuild
   * the grid config so the new selections land in `extraParams`, then
   * push that config onto the directive (which re-binds via `@Input`)
   * and force a fresh fetch via `refresh()`. `clearCache()` keeps the
   * cache from serving stale rows when the same step is revisited
   * after a filter change.
   *
   * The grid reference comes from the template's
   * `#gridRef="efileGrid"` exportAs binding — same wiring already in
   * use by `onGridCallback`. When the grid was previously gated
   * (required filter unset, autoLoad=false) we may not yet have a
   * `gridRef` because the host wasn't rendered; in that case the
   * recomputed config — now with `autoLoad: true` — will trigger the
   * initial fetch as the host mounts on the next CD pass.
   */
  applyFilters(grid: EfileGridDirective | null): void {
    if (!this.selectedStateUrl) return;
    const submission = this.activeSubmission();
    if (!submission?.SUBMISSION_ID) return;

    const leaf = this.normalizeLeaf(this.resolveLeafKey(this.selectedStateUrl)) || '';
    const base = RETURN_REVIEW_GRID_CONFIGS[leaf] || ({} as ReturnReviewGridBase);
    if (this.hasUnsatisfiedRequiredFilters(this.selectedStateUrl, base)) {
      return;
    }

    this.appliedTabFilterParams[this.selectedStateUrl] = this.collectFilterParamsFromSelections(
      this.selectedStateUrl,
    );
    this.tabFiltersCommitted[this.selectedStateUrl] = true;
    delete this.gridRowCountByStateUrl[this.selectedStateUrl];

    // Mirror legacy `EfileWorkflowController.js` L271
    // (`workspaceFactory.activeScreen.filters.filterParams = params;`):
    // commit the user's tab-level filter pick onto `activeScreen.filters`
    // so the data-filters action button bar (e.g. screen 11618 →
    // **Extract Elements from XML**) and any other consumer reading
    // `activeScreen.filters.getFilterParams()` see the latest selection.
    this.syncTabFiltersToActiveScreen();

    const newConfig = this.buildGridConfig(this.selectedStateUrl, submission);
    if (!newConfig) return;
    // Push the new config first so the directive's `@Input` binding
    // sees fresh `extraParams` next CD pass, then explicitly trigger a
    // refresh on the live directive instance (clearing the cache so
    // we don't serve stale rows captured under the previous filter).
    this.gridConfigByStateUrl[this.selectedStateUrl] = newConfig;
    this.cdr.markForCheck();
    if (grid) {
      grid.clearCache();
      grid.refresh(newConfig.extraParams);
    }
  }

  // ---------------------------------------------------------------------------
  // ctrlFn dispatch (sub-sno-disable / sub-req-validations / ...)
  // ---------------------------------------------------------------------------

  onGridCallback(event: EfileGridCallbackEvent, grid: EfileGridDirective): void {
    // Inline cell edits are persisted by EfileGridDirective via
    // GridSaveFactory.saveSwitch (savePyCyComment / saveEfileDiagComment).
    // Skip the ctrlFn dispatcher here so the comment is not saved twice.
    if (event?.type === 'input') {
      return;
    }

    const activeStep = this.currentStep();
    const stateLeaf = this.normalizeLeaf(this.resolveLeafKey(this.selectedStateUrl || ''));
    const dispatchEvent = {
      ...event,
      workflowStepId: activeStep?.WORKFLOW_STEP_ID,
      stateLeaf: stateLeaf || undefined,
    };

    const plan = this.ctrlFnDispatcher.plan(dispatchEvent);
    if (!plan) return;

    if (!plan.confirmConfig) {
      this.executePlan(plan, grid);
      return;
    }

    this.pendingPlan = plan;
    this.pendingGrid = grid;
    this.confirmModalConfig = { ...this.confirmModalConfig, ...plan.confirmConfig };
    this.showConfirmModal = true;
    this.cdr.markForCheck();
  }

  onConfirmModal(): void {
    const plan = this.pendingPlan;
    const grid = this.pendingGrid;
    this.showConfirmModal = false;
    this.pendingPlan = null;
    this.pendingGrid = null;
    this.cdr.markForCheck();
    if (plan && grid) {
      this.executePlan(plan, grid);
    }
  }

  onCancelModal(): void {
    this.showConfirmModal = false;
    this.pendingPlan = null;
    this.pendingGrid = null;
    this.cdr.markForCheck();
  }

  private executePlan(plan: CtrlFnPlan, grid: EfileGridDirective): void {
    this.subs.add(
      plan.execute().subscribe((result) => {
        if (result === 'refresh') {
          grid.refresh();
        }
      }),
    );
  }

  // ---------------------------------------------------------------------------
  // Internals
  // ---------------------------------------------------------------------------

  private computeReviewProcessFlag(): boolean {
    const serverConfig =
      (this.appService as any)?.SERVER_CONFIG ||
      (this.globalService as any)?.SERVER_CONFIG ||
      {};
    const settings = serverConfig.configSettings || {};
    const ssoId =
      this.appService.USER_SETTINGS?.user?.sso_id ||
      this.globalService.global?.globalParams?.sso_id ||
      (this.globalService as any)?.global?.inputs?.ssoId ||
      '';
    const flag = settings.enable_review_process;
    if (!flag) return true; // permissive default: don't block the UI when config is absent
    if (typeof flag === 'string') {
      if (flag.toLowerCase() === 'all') return true;
      if (!ssoId) return true;
      return !!ssoId && flag.indexOf(ssoId) !== -1;
    }
    return true;
  }

  private activeSubmission(): Submission | undefined {
    return this.submission || this.submissionService.activeSubmission;
  }
}
